import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, X } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import pub from '../i18n/publicTranslations';

const FLAGS = [
  { code: 'en', flag: '🇺🇸', label: 'EN' },
  { code: 'es', flag: '🇪🇸', label: 'ES' },
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
];

export default function PublicNav({ activePage }) {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);

  const pt = (key) => {
    const entry = pub[key];
    if (!entry) return key;
    const idx = language === 'es' ? 1 : language === 'fr' ? 2 : 0;
    return entry[idx] || entry[0];
  };

  const links = [
    { path: '/features', key: 'nav.features' },
    { path: '/pricing', key: 'nav.pricing' },
    { path: '/blog', key: 'nav.blog' },
    { path: '/track-package', key: 'nav.trackPkg' },
    { path: '/about', key: 'nav.about' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B1120]/90 backdrop-blur-md border-b border-white/5">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => navigate('/')} className="flex items-center gap-2 flex-shrink-0">
          <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-9" />
        </button>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6 text-sm">
          {links.map(l => (
            <button
              key={l.path}
              onClick={() => navigate(l.path)}
              className={`transition-colors ${activePage === l.path ? 'text-[#FF9900] font-medium' : 'text-gray-400 hover:text-white'}`}
            >
              {pt(l.key)}
            </button>
          ))}
        </div>

        {/* Right side: flags + auth */}
        <div className="flex items-center gap-2">
          {/* Language Flags */}
          <div className="flex items-center bg-white/5 rounded-lg p-0.5 border border-white/10" data-testid="language-switcher">
            {FLAGS.map(f => (
              <button
                key={f.code}
                onClick={() => setLanguage(f.code)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all ${
                  language === f.code
                    ? 'bg-[#FF9900] text-white shadow-sm'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`lang-flag-${f.code}`}
              >
                <span className="text-sm">{f.flag}</span>
                <span className="hidden sm:inline">{f.label}</span>
              </button>
            ))}
          </div>

          {/* Auth buttons - desktop */}
          <div className="hidden md:flex items-center gap-2 ml-2">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-gray-400 hover:text-white text-sm" data-testid="nav-login-btn">
              {pt('nav.login')}
            </Button>
            <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')} className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-white rounded-lg text-sm" data-testid="nav-signup-btn">
              {pt('nav.scheduleCall')}
            </Button>
          </div>

          {/* Mobile menu toggle */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 text-gray-400 hover:text-white">
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0B1120] border-t border-white/5 overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-3">
              {links.map(l => (
                <button
                  key={l.path}
                  onClick={() => { navigate(l.path); setMobileOpen(false); }}
                  className={`text-left text-sm py-2 ${activePage === l.path ? 'text-[#FF9900]' : 'text-gray-400'}`}
                >
                  {pt(l.key)}
                </button>
              ))}
              <div className="flex gap-2 pt-2 border-t border-white/5">
                <Button variant="ghost" onClick={() => { navigate('/login'); setMobileOpen(false); }} className="text-gray-400 text-sm flex-1">
                  {pt('nav.login')}
                </Button>
                <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')} className="bg-[#FF9900] text-white text-sm flex-1">
                  {pt('nav.scheduleCall')}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
